/**
 * Enhanced AdminClassStudentPage using the new component system
 * Demonstrates class student management with enhanced table and bulk operations
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiUsers, FiEdit, FiTrash2, FiSearch, FiKey } from 'react-icons/fi';

// New component system imports
import { CrudPageLayout, ConfirmModal, TableColumn } from '@grcs/components';
import {
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  Stack,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  useToast,
  Button,
  Avatar,
} from '@chakra-ui/react';

// Existing hooks and types
import {
  useClassOperations,
  useSemesterOperations,
  useStudentOperations,
  useUserOperations,
} from '@grcs/hook/useDatabase';
import { StudentProfile, ClassProfile } from '@grcs/types/DataTypes';
import { TABLE_NAMES } from '@grcs/utils/database/types';
import { useAccountManagement } from '@grcs/hook/useAccountManagement';
import { isNotUndefined } from '@grcs/utils/isNotUndefined';

interface ClassStudentStats {
  totalStudents: number;
  activeStudents: number;
  className: string;
  teacherName: string;
  semesterYear: string;
  grade: string;
}

interface EnhancedStudentProfile extends StudentProfile {
  status?: 'active' | 'inactive';
}

export const AdminClassStudentPageEnhanced: React.FC = () => {
  const classOps = useClassOperations();
  const studentOps = useStudentOperations();
  const semesterOps = useSemesterOperations();
  const userOps = useUserOperations();
  const navigate = useNavigate();
  const toast = useToast();
  const accounts = useAccountManagement();
  const [resetTarget, setResetTarget] = useState<EnhancedStudentProfile | null>(
    null,
  );
  const [isResetting, setIsResetting] = useState(false);
  const resetPending = useRef(false);
  // Session-only feedback is separate from enrollment status and roster counts.
  const [passwordResetTokens, setPasswordResetTokens] = useState<Set<string>>(
    () => new Set(),
  );

  const handleConfirmReset = async () => {
    if (!resetTarget?.student_token || resetPending.current) return;
    resetPending.current = true;
    setIsResetting(true);
    try {
      // Enrollment student_token identifies the underlying user account.
      // The shared service reads its saved birthday before resetting the password.
      await accounts.resetDefaultPassword(resetTarget.student_token);
      setPasswordResetTokens((previous) =>
        new Set(previous).add(resetTarget.student_token),
      );
      setResetTarget(null);
      toast({
        title: 'Password reset to birthday',
        description:
          'The password is now the saved birthday in YYYYMMDD format.',
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Password reset failed',
        description:
          error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      resetPending.current = false;
      setIsResetting(false);
    }
  };
  const { classId, year } = useParams();
  const [bulkTargets, setBulkTargets] = useState<
    EnhancedStudentProfile[] | null
  >(null);
  const [bulkProgress, setBulkProgress] = useState<{
    total: number;
    completed: number;
    succeeded: number;
    failures: string[];
  } | null>(null);
  const bulkGeneration = useRef(0);
  useEffect(
    () => () => {
      bulkGeneration.current++;
    },
    [classId],
  );

  const handleConfirmBulkReset = async () => {
    if (!bulkTargets?.length || resetPending.current) return;
    resetPending.current = true;
    setIsResetting(true);
    const generation = bulkGeneration.current;
    const result = {
      total: bulkTargets.length,
      completed: 0,
      succeeded: 0,
      failures: [] as string[],
    };
    setBulkProgress({ ...result });
    try {
      // Process sequentially so each account has a confirmed outcome before continuing.
      for (const student of bulkTargets) {
        if (generation !== bulkGeneration.current) break;
        try {
          await accounts.resetDefaultPassword(student.student_token);
          result.succeeded++;
          if (generation === bulkGeneration.current) {
            setPasswordResetTokens((previous) =>
              new Set(previous).add(student.student_token),
            );
          }
        } catch (error) {
          result.failures.push(
            `${student.first_name} ${student.last_name}: ${
              error instanceof Error ? error.message : 'Password reset failed.'
            }`,
          );
        }
        result.completed++;
        if (generation === bulkGeneration.current)
          setBulkProgress({ ...result, failures: [...result.failures] });
      }
    } finally {
      resetPending.current = false;
      setIsResetting(false);
      setBulkTargets(null);
    }
  };

  const [students, setStudents] = useState<EnhancedStudentProfile[]>([]);
  const [classInfo, setClassInfo] = useState<ClassProfile | null>(null);
  const [search, setSearch] = useState('');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const panelBg = useColorModeValue('gray.50', 'gray.700');
  const [currentYear, setCurrentYear] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [deleteId, setDeleteId] = useState('');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [stats, setStats] = useState<ClassStudentStats>({
    totalStudents: 0,
    activeStudents: 0,
    className: '',
    teacherName: '',
    semesterYear: '',
    grade: '',
  });

  // Load current semester year
  useEffect(() => {
    const loadCurrentYear = async () => {
      try {
        const { data } = await semesterOps.getCurrentSemesterYear();
        if (data && data.length > 0) {
          setCurrentYear(data[0].semester_year);
        }
      } catch (error) {
        console.error('Error loading current year:', error);
      }
    };

    if (!currentYear) {
      loadCurrentYear();
    }
  }, [currentYear, semesterOps]);

  // Load class information
  useEffect(() => {
    const loadClassInfo = async () => {
      if (!classId) return;

      try {
        const { data } = await classOps.getClassInfoByClassId(classId);
        if (data && data.length > 0) {
          const classData = data[0] as ClassProfile;
          setClassInfo(classData);

          let teacherName = 'Unassigned';
          // Load teacher name
          if (classData.teacher_token) {
            const { data: teacherData } =
              await userOps.getTeacherNameByTeacherToken(
                classData.teacher_token,
              );
            teacherName = teacherData?.[0]?.full_name || 'Unknown Teacher';
          }
          setStats((prev) => ({
            ...prev,
            className: classData.class_name,
            teacherName,
            semesterYear: classData.semester_year,
            grade: classData.grade,
          }));
        }
      } catch (error) {
        console.error('Error loading class info:', error);
        toast({
          title: 'Error Loading Class',
          description: 'Failed to load class information',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    loadClassInfo();
  }, [classId, classOps, userOps, toast]);

  // Load students
  useEffect(() => {
    const loadStudents = async () => {
      if (!classId) return;

      try {
        setIsLoading(true);
        const { data, error } = await studentOps.getStudentsByClassId(classId);

        if (error) {
          throw error;
        }

        const studentList = (data as StudentProfile[]) || [];
        const enhancedStudents: EnhancedStudentProfile[] = studentList
          .filter(isNotUndefined)
          .map((student) => ({
            ...student,
            status: student.deleted ? 'inactive' : 'active',
          }));

        setStudents(enhancedStudents);

        // Update class student count
        const activeCount = enhancedStudents.filter(
          (s) => s.status === 'active',
        ).length;
        if (activeCount !== classInfo?.number_of_students) {
          await classOps.updateDataById(
            TABLE_NAMES.CLASS,
            'class_token',
            classId,
            { number_of_students: activeCount },
          );
        }

        // Update stats
        setStats((prev) => ({
          ...prev,
          totalStudents: enhancedStudents.length,
          activeStudents: activeCount,
        }));
      } catch (error: any) {
        console.error('Error loading students:', error);
        toast({
          title: 'Error Loading Students',
          description: error.message || 'Failed to load student list',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadStudents();
  }, [classId, studentOps, classOps, classInfo?.number_of_students, toast]);

  // Event handlers
  const handleAddStudent = () => {
    navigate(`/admin/${year || currentYear}/class/${classId}/students/new`);
  };

  const handleEditStudent = (student: EnhancedStudentProfile) => {
    navigate(`/admin/user/${student.student_token}`);
  };

  const handleDeleteClick = (student: EnhancedStudentProfile) => {
    setDeleteId(student.student_token);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);

    try {
      // Soft delete the student
      await studentOps.updateDataById(
        TABLE_NAMES.STUDENT,
        'student_token',
        deleteId,
        { deleted: true },
      );

      // Update local state
      setStudents((prev) =>
        prev.map((s) =>
          s.student_token === deleteId
            ? { ...s, status: 'inactive', deleted: true }
            : s,
        ),
      );

      // Update stats
      setStats((prev) => ({
        ...prev,
        activeStudents: prev.activeStudents - 1,
      }));

      setIsDeleteOpen(false);

      toast({
        title: 'Student Removed',
        description: 'Student has been removed from the class',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Error deleting student:', error);
      toast({
        title: 'Error Removing Student',
        description: error.message || 'Failed to remove student from class',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRowClick = (student: EnhancedStudentProfile) => {
    // Navigate to student details or dashboard
    navigate(`/admin/user/${student.student_token}`);
  };

  const handleUpdateTeacher = () => {
    navigate(`/admin/${year || currentYear}/class/${classId}/teacher/update`);
  };

  // Filter active students for main display
  const activeStudents = students.filter((s) => s.status === 'active');
  const inactiveStudents = students.filter((s) => s.status === 'inactive');
  const visibleStudents = activeStudents.filter((student) =>
    `${student.first_name} ${student.last_name} ${student.email || ''} ${
      student.phone_number || ''
    }`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  const displayYear = year || currentYear;

  // Table columns definition
  const columns: TableColumn<EnhancedStudentProfile>[] = [
    {
      key: 'first_name',
      header: 'Student',
      render: (student: EnhancedStudentProfile, value: string) => (
        <HStack spacing={3}>
          <Avatar
            size="sm"
            name={`${student.first_name} ${student.last_name}`}
            bg="blue.500"
          />
          <Text fontSize="sm" fontWeight="semibold">
            {student.first_name} {student.last_name}
          </Text>
        </HStack>
      ),
    },
    {
      key: 'email',
      header: 'Contact',
      render: (student: EnhancedStudentProfile, value: string) => (
        <Box>
          <Text fontSize="sm" overflowWrap="anywhere">
            {value || 'No email provided'}
          </Text>
          {student.phone_number && (
            <Text fontSize="xs" color={muted} mt={0.5}>
              {student.phone_number}
            </Text>
          )}
        </Box>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (student: EnhancedStudentProfile, value: string) => (
        <Badge
          colorScheme={
            value !== 'active'
              ? 'red'
              : passwordResetTokens.has(student.student_token)
              ? 'blue'
              : 'green'
          }
          variant="subtle"
        >
          {value !== 'active'
            ? 'Inactive'
            : passwordResetTokens.has(student.student_token)
            ? 'Password reset'
            : 'Active'}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <CrudPageLayout
        compact
        title={
          stats.className ? `${stats.className} · Students` : 'Class Students'
        }
        subtitle="Manage enrollment and student details."
        headerBadge={
          displayYear
            ? { text: `Semester ${displayYear}`, colorScheme: 'blue' }
            : undefined
        }
        headerActions={
          <HStack flexWrap="wrap">
            <Button
              size="sm"
              variant="outline"
              leftIcon={<FiKey />}
              isDisabled={isLoading || isResetting || !activeStudents.length}
              onClick={() => {
                if (resetPending.current) return;
                setResetTarget(null);
                // Confirm the whole active roster, regardless of search or pagination.
                setBulkTargets([
                  ...new Map(
                    activeStudents.map((student) => [
                      student.student_token,
                      student,
                    ]),
                  ).values(),
                ]);
              }}
            >
              Reset all passwords
            </Button>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<FiEdit />}
              onClick={handleUpdateTeacher}
            >
              Update Teacher
            </Button>
          </HStack>
        }
        breadcrumb={{
          items: [
            { label: 'Admin', href: '/admin' },
            { label: 'Classes', href: `/admin/${displayYear}/class` },
            { label: stats.className || 'Students', isCurrent: true },
          ],
        }}
        createButton={{
          label: 'Add Student',
          onClick: handleAddStudent,
        }}
        table={{
          data: visibleStudents,
          columns,
          loading: isLoading,
          clickableRows: true,
          onRowClick: handleRowClick,
          actions: [
            {
              label: 'Edit',
              icon: <FiEdit />,
              onClick: handleEditStudent,
              variant: 'secondary',
            },
            {
              label: 'Reset password',
              icon: <FiKey />,
              onClick: (student: EnhancedStudentProfile) => {
                if (!resetPending.current) setResetTarget(student);
              },
              variant: 'secondary',
            },
            {
              label: 'Remove',
              icon: <FiTrash2 />,
              onClick: handleDeleteClick,
              variant: 'danger',
            },
          ],
          emptyState: (
            <VStack spacing={2} py={8} color={muted} textAlign="center">
              <FiUsers size={28} />
              <Text fontWeight="semibold">
                {search.trim()
                  ? 'No matching students'
                  : 'No students in this class'}
              </Text>
              <Text fontSize="sm">
                {search.trim()
                  ? 'Try another name, email, or phone number.'
                  : 'Use Add Student to enroll students in this class.'}
              </Text>
              {search.trim() && (
                <Button size="sm" variant="ghost" onClick={() => setSearch('')}>
                  Clear search
                </Button>
              )}
            </VStack>
          ),
        }}
      >
        {bulkProgress && (
          <Box role="status" borderWidth="1px" borderRadius="md" p={3}>
            <Text fontWeight="semibold">
              {bulkProgress.completed < bulkProgress.total
                ? 'Resetting passwords'
                : 'Password resets finished'}
              : {bulkProgress.completed} of {bulkProgress.total} processed
            </Text>
            <Text fontSize="sm">
              {bulkProgress.succeeded} reset · {bulkProgress.failures.length}{' '}
              failed
            </Text>
            {bulkProgress.failures.length > 0 && (
              <Box mt={2}>
                <Text fontSize="sm">
                  Retry failed students using their individual Reset password
                  action.
                </Text>
                <Box as="ul" pl={5} fontSize="sm">
                  {bulkProgress.failures.map((failure, index) => (
                    <li key={index}>{failure}</li>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
        <Stack
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          spacing={3}
          bg={panelBg}
          px={4}
          py={3}
          borderRadius="md"
        >
          <Box minW={0}>
            <Text fontSize="xs" color={muted}>
              Assigned teacher
            </Text>
            <HStack spacing={2} flexWrap="wrap" rowGap={1}>
              <Text fontSize="sm" fontWeight="semibold">
                {stats.teacherName || 'Loading...'}
              </Text>
              {stats.grade && (
                <Badge colorScheme="blue" borderRadius="sm">
                  Grade {stats.grade}
                </Badge>
              )}
            </HStack>
          </Box>
          <HStack spacing={5}>
            <Box>
              <Text fontSize="xs" color={muted}>
                Active students
              </Text>
              <Text fontSize="lg" fontWeight="semibold">
                {isLoading ? '—' : activeStudents.length}
              </Text>
            </Box>
            <Box>
              <Text fontSize="xs" color={muted}>
                Total listed
              </Text>
              <Text fontSize="lg" fontWeight="semibold">
                {isLoading ? '—' : students.length}
              </Text>
            </Box>
          </HStack>
        </Stack>

        {inactiveStudents.length > 0 && (
          <Box as="details" borderWidth="1px" borderRadius="md" px={4} py={3}>
            <Box
              as="summary"
              cursor="pointer"
              fontSize="sm"
              fontWeight="medium"
            >
              Removed students ({inactiveStudents.length})
            </Box>
            <Text fontSize="xs" color={muted} mt={2}>
              Removed from this class; their user accounts remain in the system.
            </Text>
            <VStack spacing={2} align="stretch" mt={3}>
              {inactiveStudents.map((student) => (
                <HStack
                  key={student.student_token}
                  justify="space-between"
                  flexWrap="wrap"
                  spacing={3}
                  rowGap={2}
                >
                  <Text fontSize="sm">
                    {student.first_name} {student.last_name}
                  </Text>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => handleEditStudent(student)}
                  >
                    View Details
                  </Button>
                </HStack>
              ))}
            </VStack>
          </Box>
        )}

        <Stack
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          spacing={2}
        >
          <InputGroup size="sm" maxW={{ md: '360px' }}>
            <InputLeftElement pointerEvents="none">
              <FiSearch />
            </InputLeftElement>
            <Input
              type="search"
              aria-label="Search class students"
              placeholder="Search name, email, or phone"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              borderRadius="md"
            />
          </InputGroup>
          <Text fontSize="xs" color={muted} role="status">
            {isLoading
              ? 'Loading students…'
              : `${visibleStudents.length} of ${activeStudents.length} active students`}
          </Text>
        </Stack>
      </CrudPageLayout>

      <ConfirmModal
        isOpen={!!bulkTargets}
        onClose={() => {
          if (!resetPending.current) setBulkTargets(null);
        }}
        type="warning"
        title="Reset all class passwords"
        message={`Reset passwords for all ${
          bulkTargets?.length || 0
        } active students in ${stats.className || 'this class'}?`}
        description="Each password will be replaced with that student's saved birthday (YYYYMMDD). This includes students hidden by search or pagination and excludes removed students. Successful resets take effect immediately; failures do not undo them."
        onConfirm={handleConfirmBulkReset}
        confirmText="Reset all passwords"
        loading={isResetting}
      />
      <ConfirmModal
        isOpen={!!resetTarget}
        onClose={() => {
          if (!resetPending.current) setResetTarget(null);
        }}
        type="warning"
        title="Reset password to birthday"
        message={`Reset the password for ${resetTarget?.first_name || ''} ${
          resetTarget?.last_name || ''
        } (${resetTarget?.email || ''})?`}
        description="This replaces the current password with the saved birthday in YYYYMMDD format and takes effect immediately."
        onConfirm={handleConfirmReset}
        confirmText="Reset password"
        loading={isResetting}
      />
      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        type="danger"
        title="Remove Student from Class"
        message="Are you sure you want to remove this student from the class?"
        description="This will not delete the student account, but will remove them from this specific class. They can be re-added later if needed."
        onConfirm={handleConfirmDelete}
        confirmText="Remove Student"
        loading={isDeleting}
      />
    </>
  );
};
